import React, {useEffect, useState} from 'react';
import {Btn} from '../../shared/elements/btn/Btn';
import {MainContent} from '../../shared/grid/main-content/MainContentFeatures.js';
import {Card} from '../../shared/elements/card/Card.js';
import {StackedSpacer} from '../../shared/custom-features/spacer/StackedSpacer.js';
import {withTranslation} from 'react-i18next';
import {
    loadMendriXStatus,
    retrieveMendriXData,
    terminateMendriXRetrieval,
    downloadAllResults,
    getJsonFilesList,
} from './endpoints.js';
import {LineSpacer} from '../../shared/custom-features/spacer/LineSpacer.js';
import {FormSelect} from '../../shared/form/form-control/FormSelect.js';
import {FormFile} from '../../shared/form/form-control/FormFile.js';
import errorHandler from '../../util/errorHandler.js';
import {saveAs} from 'file-saver';
import moment from 'moment';
import 'moment-duration-format';
import 'moment/locale/nl';
import {useFirstRender} from '../../util/useFirstRender.js';

const weeks = _.map(_.range(-52, 1), (week) => ({
    start: moment().startOf('week').add(week, 'weeks').format('DD-MM-YYYY'),
    end: moment().endOf('week').add(week, 'weeks').format('DD-MM-YYYY'),
    number: moment().startOf('week').add(week, 'weeks').week(),
    year: moment().startOf('week').add(week, 'weeks').format('YYYY'),
}));

function getKey(filename) {
    const [weekNumber, startDate, endDate] = filename.replace('.json', '').split('_');
    return `Week ${weekNumber} : ${startDate} tot ${endDate}`;
}

const CseTool = ({t}) => {
    const [mendrixData, setMendrixData] = useState({});
    const [resultsFile, setResultsFile] = useState(null);
    const [kmsFile, setKmsFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [mendrixDataWeek, setMendriXDataWeek] = useState({
        start: moment().startOf('week').add(-1, 'week').format('DD-MM-YYYY'),
        end: moment().endOf('week').add(-1, 'week').format('DD-MM-YYYY'),
        number: moment().startOf('week').add(-1, 'week').week(),
        year: moment().startOf('week').add(-1, 'week').format('YYYY'),
    });
    const [fileName, setFileName] = useState();
    const [filesList, setFilesList] = useState([]);
    const firstRender = useFirstRender();

    const checkMendriXStatus = async () => {
        const resp = await loadMendriXStatus(mendrixDataWeek.start);
        const output = resp.data.output;
        const status = resp.data.customStatus;
        if (status === 'Finished') {
            setMendrixData({status: 'Finished', output: t('mendrix.Finished')});
        } else if (output?.startsWith('Orchestrator function')) {
            setMendrixData({status: 'Failed', output: t('mendrix.SomethingWentWrong')});
        } else if (output === 'CancelledByUser') {
            setMendrixData({status: 'Failed', output: t('mendrix.CancelledByUser')});
        } else {
            setMendrixData({status: status, output: output});
        }
    };

    const onUploadResult = (event) => {
        setResultsFile(event.target.files[0]);
    };

    const onUploadKms = (event) => {
        setKmsFile(event.target.files[0]);
    };

    useEffect(() => {
        if (firstRender || mendrixData.status === 'Finished') {
            getJsonFilesList()
                .then((res) => {
                    let filenames = res.data.filter((f) => f.indexOf('_') !== -1);

                    filenames.sort((a, b) => {
                        const extractDate = (str) => {
                            const match = str.match(/_(\d{2}-\d{2}-\d{4}|\d{2}-\d{2}-\d{2})_/);
                            if (!match) return new Date(0); // fallback in case of no match
                            let [day, month, year] = match[1].split('-');
                            year = year.length === 2 ? `20${year}` : year; // assume 20xx for 2-digit years
                            return new Date(`${year}-${month}-${day}`);
                        };

                        return extractDate(a) - extractDate(b);
                    });

                    setFilesList(filenames);
                    setFileName(filenames[filenames.length - 1]);
                })
                .catch((e) => {
                    errorHandler(e.response.data, true, t);
                });
        }
    }, [mendrixData.status]);

    const downloadAllFinalResults = () => {
        setLoading(true);

        const [weekNumber, weekStart] = fileName.replace('.json', '').split('_');

        downloadAllResults(weekStart, fileName, resultsFile, kmsFile)
            .then((res) => {
                let extension = 'zip';
                let tempFileName = `All-results-week-${weekNumber}-${weekStart}`;
                let fileName = `${tempFileName}.${extension}`;

                const blob = new Blob([res.data], {
                    type: 'application/octet-stream',
                });
                saveAs(blob, fileName);
            })
            .catch((e) => {
                errorHandler(e.response.data, true, t);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const beginToRetrieveAndCheck =
        !_.isEmpty(mendrixData) && mendrixData.status !== 'Failed' && mendrixData.status !== 'Finished';

    useEffect(async () => {
        if (beginToRetrieveAndCheck) await checkMendriXStatus();
    }, []);

    const isResultsTemplatesUploaded = resultsFile !== null && kmsFile !== null;

    return (
        <MainContent.Body>
            <StackedSpacer space="md">
                <Card color="primary">
                    <h3>{t('explanations.MendriX')}</h3>
                    <br />
                    <LineSpacer space="sm">
                        <FormSelect
                            defaultValue={mendrixDataWeek.start}
                            label={t('general.SelectWeek')}
                            onChange={(evt) => setMendriXDataWeek(weeks.find((week) => week.start == evt.target.value))}
                        >
                            {weeks.map((week) => {
                                return (
                                    <option value={week.start} key={week.start}>
                                        Week {week.number} : {week.start} tot {week.end}
                                    </option>
                                );
                            })}
                        </FormSelect>
                    </LineSpacer>
                </Card>
                <Btn
                    type="button"
                    color="success"
                    onClick={async () => {
                        await retrieveMendriXData(mendrixDataWeek.start);
                        setMendrixData({status: 'Starting'});
                    }}
                    disabled={beginToRetrieveAndCheck}
                >
                    {t('mendrix.RetrieveDataShort')}
                </Btn>
                <div>
                    <Btn type="button" color="success" onClick={checkMendriXStatus}>
                        {t('mendrix.CheckStatus')}
                    </Btn>
                    {mendrixData.output ?? mendrixData.status}
                </div>
                <Btn
                    type="button"
                    color="danger"
                    onClick={async () => {
                        await terminateMendriXRetrieval(mendrixDataWeek.start);
                        await checkMendriXStatus();
                    }}
                >
                    {t('mendrix.TerminateRetrieval')}
                </Btn>
            </StackedSpacer>
            <br />
            <StackedSpacer space="md">
                <Card color="primary">
                    <h3>{t('explanations.DownloadAllResults')}</h3>
                    <br />
                    <LineSpacer space="sm">
                        {filesList.length === 0 ? (
                            <></>
                        ) : (
                            <FormSelect
                                label={t('general.SelectWeek')}
                                defaultValue={filesList[filesList.length - 1]}
                                onChange={(evt) => setFileName(evt.target.value)}
                            >
                                {filesList.map((filePath) => {
                                    return (
                                        <option value={filePath} key={filePath}>
                                            {getKey(filePath)}
                                        </option>
                                    );
                                })}
                            </FormSelect>
                        )}
                    </LineSpacer>
                    <br />
                    <h3>{t('explanations.UploadTemplateFiles')}</h3>
                    <ol>
                        <li>{t('explanations.JustNeedsExcel')}</li>
                    </ol>
                </Card>
                <LineSpacer space="sm">
                    {!loading && (
                        <FormFile
                            label={t('resultsOverview.UploadResultsFile')}
                            accept=".xlsx"
                            onChange={onUploadResult}
                            fileName={resultsFile && resultsFile.name}
                        />
                    )}
                </LineSpacer>
                <LineSpacer space="sm">
                    {!loading && (
                        <FormFile
                            label={t('kms.UploadKmsFile')}
                            accept=".xlsx"
                            onChange={onUploadKms}
                            fileName={kmsFile && kmsFile.name}
                        />
                    )}
                </LineSpacer>
                {isResultsTemplatesUploaded && (
                    <Btn type="button" color="success" onClick={downloadAllFinalResults} disabled={loading}>
                        {loading ? t('general.Busy') : t('general.Generate')}
                    </Btn>
                )}
            </StackedSpacer>
        </MainContent.Body>
    );
};
export default withTranslation()(CseTool);
